import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Play, Clock, Video, Loader2, ExternalLink, Calendar, Crown, Youtube, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// VIP Live Classes Page
export const VipClassesPage = () => {
  const { isVip } = useAuth();
  const navigate = useNavigate();

  // Real class schedule with Zoom links
  const weeklyClasses = [
    {
      id: 'fusion-flow',
      title: 'Fusion Flow',
      description: 'A dynamic blend of movement styles to energize your body and spirit. Get ready to flow!',
      day: 'Every Tuesday',
      time: '11:30 AM ET',
      duration: '45 minutes',
      zoomLink: 'https://us02web.zoom.us/j/83709083043?pwd=f1EkBNAOoOqRMOvf7CF9KI6BUnBcBh.1',
      color: 'from-purple-500 to-pink-500',
      icon: '🌊'
    },
    {
      id: 'chair-yoga',
      title: 'Chair Yoga',
      description: 'A gentle, accessible yoga practice using a chair for support. Perfect for all levels and abilities.',
      day: 'Every Friday',
      time: '11:30 AM ET',
      duration: '60 minutes',
      zoomLink: 'https://us02web.zoom.us/j/84467206990?pwd=oIXmu1YSFhrhtZsLDWlOhVNNLH40Sn.1',
      color: 'from-teal-500 to-cyan-500',
      icon: '🪑'
    }
  ];

  useEffect(() => {
    if (!isVip) {
      navigate('/pricing');
      toast.error('VIP membership required for live classes');
    }
  }, [isVip, navigate]);

  const handleJoinClass = (classItem) => {
    window.open(classItem.zoomLink, '_blank');
    toast.success(`Joining ${classItem.title}...`);
  };

  if (!isVip) {
    return null;
  }

  return (
    <div className="space-y-8" data-testid="vip-classes-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-stone-900">Live Movement Classes</h1>
          <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
            <Crown className="w-3 h-3" /> VIP
          </span>
        </div>
        <p className="text-stone-600">Join Heather twice weekly for live movement sessions on Zoom</p>
      </div>

      {/* Weekly Schedule Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {weeklyClasses.map((classItem) => (
          <div 
            key={classItem.id}
            className="card-cosmic overflow-hidden border-2 border-amber-500/20 hover:border-amber-500/40 transition-all"
            data-testid={`live-class-${classItem.id}`}
          >
            {/* Header with gradient */}
            <div className={`bg-gradient-to-r ${classItem.color} p-6 text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-4xl mb-2 block">{classItem.icon}</span>
                  <h3 className="text-2xl font-bold">{classItem.title}</h3>
                </div>
                <div className="text-right">
                  <div className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium">
                    LIVE
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-stone-600 mb-6">{classItem.description}</p>
              
              {/* Schedule Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-stone-700">
                  <Calendar className="w-5 h-5 text-amber-500" />
                  <span className="font-medium">{classItem.day}</span>
                </div>
                <div className="flex items-center gap-3 text-stone-700">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span>{classItem.time} • {classItem.duration}</span>
                </div>
              </div>

              {/* Join Button */}
              <Button
                onClick={() => handleJoinClass(classItem)}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                data-testid={`join-${classItem.id}-btn`}
              >
                <Video className="w-4 h-4 mr-2" />
                Join on Zoom
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Info Section */}
      <div className="card-cosmic p-6 bg-amber-50/50 border border-amber-200">
        <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-amber-500" />
          How to Join
        </h3>
        <ul className="space-y-2 text-sm text-stone-600">
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">•</span>
            Click "Join on Zoom" a few minutes before class starts
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">•</span>
            Make sure you have space to move and a mat if available
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">•</span>
            Camera is optional - participate however feels comfortable
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">•</span>
            Can't make it live? Check the Class Library for recordings
          </li>
        </ul>
      </div>

      {/* Quick Link to Library */}
      <div className="text-center">
        <Button
          onClick={() => navigate('/dashboard/class-library')}
          variant="outline"
          className="border-2 border-amber-500 text-amber-600 hover:bg-amber-50"
        >
          <Play className="w-4 h-4 mr-2" />
          Browse Recorded Class Library
        </Button>
      </div>
    </div>
  );
};

// Class Library Page (recorded classes from YouTube)
export const ClassLibraryPage = () => {
  const [fusionFlowClasses, setFusionFlowClasses] = useState([]);
  const [chairYogaClasses, setChairYogaClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('fusion_flow');
  const { getAuthHeader, isVip } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isVip) {
      navigate('/pricing');
      toast.error('VIP membership required for the class library');
      return;
    }
    fetchClasses();
  }, [isVip]);

  const fetchClasses = async () => {
    try {
      // Fetch VIP classes from YouTube playlists
      const response = await axios.get(`${API_URL}/vip/classes`, {
        headers: getAuthHeader()
      });
      setFusionFlowClasses(response.data.fusion_flow || []);
      setChairYogaClasses(response.data.chair_yoga || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      if (error.response?.status === 403) {
        navigate('/pricing');
        toast.error('VIP membership required for the class library');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWatchVideo = (video) => {
    setSelectedVideo(video);
  };

  const currentClasses = activeTab === 'fusion_flow' ? fusionFlowClasses : chairYogaClasses;
  const totalClasses = fusionFlowClasses.length + chairYogaClasses.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="class-library-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-stone-900">Class Library</h1>
          <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
            <Crown className="w-3 h-3" /> VIP
          </span>
        </div>
        <p className="text-stone-600">Watch past classes anytime - {totalClasses} recordings available</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-4">
        <button
          onClick={() => setActiveTab('fusion_flow')}
          className={`px-8 py-4 font-semibold text-base rounded-xl transition-all ${
            activeTab === 'fusion_flow'
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          }`}
          data-testid="fusion-flow-tab"
        >
          <span className="mr-2 text-lg">🌊</span>
          Fusion Flow
          <span className={`ml-3 text-sm px-2 py-1 rounded-full ${
            activeTab === 'fusion_flow' 
              ? 'bg-white/20' 
              : 'bg-purple-100 text-purple-600'
          }`}>
            {fusionFlowClasses.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('chair_yoga')}
          className={`px-8 py-4 font-semibold text-base rounded-xl transition-all ${
            activeTab === 'chair_yoga'
              ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg'
              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          }`}
          data-testid="chair-yoga-tab"
        >
          <span className="mr-2 text-lg">🪑</span>
          Chair Yoga
          <span className={`ml-3 text-sm px-2 py-1 rounded-full ${
            activeTab === 'chair_yoga' 
              ? 'bg-white/20' 
              : 'bg-teal-100 text-teal-600'
          }`}>
            {chairYogaClasses.length}
          </span>
        </button>
      </div>

      {/* Class Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentClasses.map((classItem, index) => (
          <div 
            key={classItem.video_id || index}
            className="card-cosmic overflow-hidden card-hover group cursor-pointer"
            onClick={() => handleWatchVideo(classItem)}
            data-testid={`library-class-${index}`}
          >
            <div className="aspect-video relative overflow-hidden">
              <img 
                src={classItem.thumbnail_url || '/placeholder-class.jpg'}
                alt={classItem.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-8 h-8 text-amber-500 ml-1" />
                </div>
              </div>
              <div className="absolute top-3 left-3">
                <span className={`text-white text-xs font-semibold px-2 py-1 rounded-full ${
                  activeTab === 'fusion_flow' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                    : 'bg-gradient-to-r from-teal-500 to-cyan-500'
                }`}>
                  {activeTab === 'fusion_flow' ? '🌊 Fusion Flow' : '🪑 Chair Yoga'}
                </span>
              </div>
              {classItem.duration && (
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  {classItem.duration}
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-stone-900 line-clamp-2 group-hover:text-amber-600 transition-colors">
                {classItem.title}
              </h3>
              {classItem.published_at && (
                <p className="text-sm text-stone-500 mt-1">
                  {new Date(classItem.published_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {currentClasses.length === 0 && (
        <div className="card-cosmic p-12 text-center">
          <Video className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <p className="text-stone-600">No {activeTab === 'fusion_flow' ? 'Fusion Flow' : 'Chair Yoga'} classes available yet.</p>
        </div>
      )}

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Youtube className="w-5 h-5 text-red-500" />
              {selectedVideo?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black">
            {selectedVideo && (
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1`}
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
